import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FiInfo, FiMapPin, FiAlertTriangle, FiClock } from 'react-icons/fi';
import LoadingSpinner from '../components/common/LoadingSpinner';
import useVehicleTracking from '../hooks/useVehicleTracking';

const Vehicles = () => {
  const [loading, setLoading] = useState(true);
  const [initialVehicles, setInitialVehicles] = useState([]);
  const { vehicles, isConnected } = useVehicleTracking(initialVehicles);

  useEffect(() => {
    // Fetch vehicle data
    const fetchVehicles = async () => {
      try {
        setLoading(true);
        
        // Simulate API delay
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Dummy data
        const dummyVehicles = [
          {
            id: 'v1',
            name: 'Truck 101',
            registration: 'ABC-1234',
            currentPosition: { lat: 40.7128, lng: -74.006, timestamp: new Date().toISOString() },
            currentSpeed: 35,
            status: 'active',
            lastUpdate: new Date().toISOString(),
            driver: { name: 'John Doe', phone: '555-1234' },
            vehicleType: 'truck',
            fuelLevel: 75
          },
          {
            id: 'v2',
            name: 'Delivery Van 202',
            registration: 'XYZ-5678',
            currentPosition: { lat: 40.7148, lng: -74.006, timestamp: new Date().toISOString() },
            currentSpeed: 0,
            status: 'idle',
            lastUpdate: new Date().toISOString(),
            driver: { name: 'Jane Smith', phone: '555-5678' },
            vehicleType: 'van',
            fuelLevel: 45
          },
          {
            id: 'v3',
            name: 'Fleet Car 303',
            registration: 'DEF-9012',
            currentPosition: { lat: 40.7118, lng: -74.009, timestamp: new Date().toISOString() },
            currentSpeed: 45,
            status: 'active',
            lastUpdate: new Date().toISOString(),
            driver: { name: 'Sam Wilson', phone: '555-9012' },
            vehicleType: 'car',
            fuelLevel: 90
          }
        ];
        
        // Set initial vehicles for the tracking hook
        setInitialVehicles(dummyVehicles);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching vehicles:', err);
        setLoading(false);
      }
    };

    fetchVehicles();
  }, []);

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'active': return 'bg-ui-success text-white';
      case 'idle': return 'bg-ui-warning text-gray-800';
      case 'maintenance': return 'bg-ui-error text-white';
      default: return 'bg-gray-500 text-white';
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-full">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Vehicles</h1>
          <p className="text-gray-600">Manage and track your fleet vehicles</p>
        </div>
        
        {/* Connection status */}
        <div className="flex items-center text-sm">
          <span className={`inline-block w-3 h-3 rounded-full mr-2 ${isConnected ? 'bg-ui-success' : 'bg-ui-error'}`}></span>
          <span>{isConnected ? 'Connected' : 'Disconnected'}</span>
        </div>
      </div>

      {/* Vehicle list */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Vehicle
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Driver
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Speed
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Fuel
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Last Updated
              </th>
              <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {vehicles.map((vehicle) => {
              const lastUpdate = new Date(vehicle.lastUpdate);
              return (
                <tr key={vehicle.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{vehicle.name}</div>
                        <div className="text-sm text-gray-500">{vehicle.registration}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeClass(vehicle.status)}`}>
                      {vehicle.status === 'active' ? 'Active' : 
                       vehicle.status === 'idle' ? 'Idle' : 
                       vehicle.status === 'maintenance' ? 'Maintenance' : 'Unknown'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{vehicle.driver?.name || 'Unassigned'}</div>
                    <div className="text-sm text-gray-500">{vehicle.driver?.phone || '-'}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {vehicle.currentSpeed} mph
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="w-full bg-gray-200 rounded-full h-2.5">
                      <div className={`h-2.5 rounded-full ${vehicle.fuelLevel > 75 ? 'bg-ui-success' : 
                                                             vehicle.fuelLevel > 25 ? 'bg-ui-warning' : 
                                                             'bg-ui-error'}`} 
                           style={{ width: `${vehicle.fuelLevel}%` }}></div>
                    </div>
                    <div className="text-xs text-gray-500 mt-1">{vehicle.fuelLevel}%</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <div className="flex items-center">
                      <FiClock className="mr-1" /> 
                      {lastUpdate.toLocaleTimeString()} ({Math.floor((new Date() - lastUpdate) / (1000 * 60))} min ago)
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-end space-x-3">
                      <Link to={`/vehicles/${vehicle.id}`} className="text-primary-600 hover:text-primary-900">
                        <FiInfo title="View Details" />
                      </Link>
                      <Link to={`/vehicles/${vehicle.id}`} className="text-blue-600 hover:text-blue-900">
                        <FiMapPin title="View on Map" />
                      </Link>
                      <Link to={`/alerts?vehicle=${vehicle.id}`} className="text-yellow-600 hover:text-yellow-900">
                        <FiAlertTriangle title="View Alerts" />
                      </Link>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Vehicles;